import {
	AlertCircle,
	AlertTriangle,
	Building2,
	CalendarDays,
	CheckCircle2,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Clock,
	Link2,
	Loader2,
	Plus,
	PoundSterling,
	Receipt,
	RefreshCw,
	RotateCcw,
	Save,
	Search,
	ShoppingCart,
	Trash2,
	UserPlus,
	UserRound,
	Users,
	X,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type {
	SyncLogPageInfo,
	SyncLogSectionHandle,
} from "@/components/integrations/SyncLogSection";
import SyncLogSection from "@/components/integrations/SyncLogSection";
import { useSyncNotification } from "@/components/integrations/SyncNotificationProvider";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
	addUserToCompany,
	createCompany,
	getAvailableDatabases,
	getCompanyUsers,
	getFinOpsInstanceByName,
	removeIntegration,
	removeUserFromCompany,
	resyncIntegration,
	searchUsers,
	syncIntegration,
	testConnection,
	updateCompany,
	updateIntegrations,
} from "@/services/integrationsRedesignService";
import type {
	AvailableDatabase,
	CompanyUser,
	CompanyWithIntegrations,
	FinanceProduct,
	IntegrationConnectionStatus,
	SimpleIntegrationConfig,
	UpdateIntegrationsPayload,
} from "@/types/integrations-redesign";
import {
	EXPENSE_PRODUCT_LABELS,
	FINANCE_PRODUCT_LABELS,
	PLANNER_PRODUCT_LABELS,
	PURCHASING_PRODUCT_LABELS,
	TIMESHEET_PRODUCT_LABELS,
} from "@/types/integrations-redesign";

// ---------------------------------------------------------------------------
// Non-finance "Sync Now" deprecation
// ---------------------------------------------------------------------------

// TEMPORARY: non-finance "Sync Now" performs no real data pull (data arrives via
// live proxy / webhooks), so the button is being deprecated. It is disabled (not
// hidden) with an explanatory tooltip so users understand it will be removed soon.
// To RE-ENABLE the old behaviour, set this to false — this restores the enabled
// "Sync Now" button and the "Last synced" label with no other changes required.
const NON_FINANCE_SYNC_DEPRECATED = true;

const SYNC_DEPRECATED_TOOLTIP =
	"Sync Now is being retired. This integration receives data automatically, so a manual sync isn't needed. Use Test Connection to verify the connection.";

// ---------------------------------------------------------------------------
// FinOps instance GUID visibility
// ---------------------------------------------------------------------------

// The FinOps instance id is a developer/diagnostic value (a GUID only, so it is
// intentionally low-security). The two display modes are mutually exclusive:
//   true  — the inline "FinOps instance: {guid}" row is shown on the purchasing
//           integration card and the dev shortcut is disabled (a Ctrl+Shift+Click
//           on the Connected pill does nothing).
//   false — the inline row is hidden and the Ctrl+Shift+Left-Click dev shortcut on
//           the purchasing Connected pill opens a modal showing the GUID.
const SHOW_FINOPS_INSTANCE_ID = true;

// ---------------------------------------------------------------------------
// Suite coupling constants
// ---------------------------------------------------------------------------

const SUITE_FINANCE_PRODUCTS: FinanceProduct[] = [
	"access-financials",
	"access-dimensions",
];

// ---------------------------------------------------------------------------
// Draft types for lifted state
// ---------------------------------------------------------------------------

interface IntegrationDraft {
	product: string;
	databaseId: string;
}

interface SheetDraft {
	companyName: string;
	companyCode: string;
	isActive: boolean;
	finance: IntegrationDraft;
	timesheet: IntegrationDraft;
	expense: IntegrationDraft;
	planner: IntegrationDraft;
	purchasing: IntegrationDraft;
}

type IntegrationType =
	| "finance"
	| "timesheet"
	| "expense"
	| "planner"
	| "purchasing";

function buildEmptyIntegrationDraft(): IntegrationDraft {
	return { product: "", databaseId: "" };
}

function buildDraftFromCompany(company: CompanyWithIntegrations): SheetDraft {
	return {
		companyName: company.name,
		companyCode: company.code,
		isActive: company.status === "active",
		finance: {
			product: company.financeIntegration?.product ?? "",
			databaseId: company.financeIntegration?.databaseId ?? "",
		},
		timesheet: {
			product: company.timesheetIntegration?.product ?? "",
			databaseId: company.timesheetIntegration?.databaseId ?? "",
		},
		expense: {
			product: company.expenseIntegration?.product ?? "",
			databaseId: company.expenseIntegration?.databaseId ?? "",
		},
		planner: {
			product: company.plannerIntegration?.product ?? "",
			databaseId: company.plannerIntegration?.databaseId ?? "",
		},
		purchasing: {
			product: company.purchasingIntegration?.product ?? "",
			databaseId: company.purchasingIntegration?.databaseId ?? "",
		},
	};
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CompanySheetProps {
	company: CompanyWithIntegrations | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCompanyUpdated: (updated: CompanyWithIntegrations) => void;
	onCompanyCreated: (created: CompanyWithIntegrations) => void;
	existingCodes?: string[];
}

// ---------------------------------------------------------------------------
// Helper: formatLastSync
// ---------------------------------------------------------------------------

function formatLastSync(lastSyncAt: string | null): string {
	if (!lastSyncAt) return "Never";
	return new Date(lastSyncAt).toLocaleString(undefined, {
		dateStyle: "short",
		timeStyle: "short",
	});
}

// ---------------------------------------------------------------------------
// Helper: ConnectionStatusBadge (local — avoids shared-file conflicts)
// ---------------------------------------------------------------------------

function ConnectionStatusBadge({
	status,
	errorMessage,
	onErrorClick,
	onConnectedClick,
}: {
	status: IntegrationConnectionStatus;
	errorMessage?: string | null;
	onErrorClick?: () => void;
	onConnectedClick?: (e: React.MouseEvent) => void;
}) {
	switch (status) {
		case "connected": {
			const badge = (
				<Badge
					variant="outline"
					className="border-success-500 text-success-700 bg-success-50 gap-1 text-xs"
				>
					<CheckCircle2 className="h-3 w-3" />
					Connected
				</Badge>
			);
			if (!onConnectedClick) return badge;
			return (
				<button
					type="button"
					onClick={onConnectedClick}
					tabIndex={-1}
					className="rounded-full cursor-default focus:outline-none"
				>
					{badge}
				</button>
			);
		}
		case "error":
			return (
				<button
					type="button"
					onClick={onErrorClick}
					title={errorMessage ?? "View error details"}
					aria-label="View integration error details"
					className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				>
					<Badge
						variant="destructive"
						className="gap-1 text-xs cursor-pointer hover:opacity-90"
					>
						<AlertCircle className="h-3 w-3" />
						Error
					</Badge>
				</button>
			);
		case "not_connected":
			return (
				<Badge variant="secondary" className="gap-1 text-xs">
					<XCircle className="h-3 w-3" />
					Not Connected
				</Badge>
			);
		case "not_configured":
			return (
				<Badge
					variant="outline"
					className="text-muted-foreground gap-1 text-xs"
				>
					Not Configured
				</Badge>
			);
		default:
			return null;
	}
}

// ---------------------------------------------------------------------------
// Integration health summary
// ---------------------------------------------------------------------------

function IntegrationHealthSummary({
	company,
}: {
	company: CompanyWithIntegrations;
}) {
	const integrations = [
		{ label: "Finance", status: company.financeIntegration.status },
		{ label: "Timesheets", status: company.timesheetIntegration.status },
		{ label: "Expense", status: company.expenseIntegration.status },
		{ label: "Scheduler", status: company.plannerIntegration.status },
		{ label: "Purchasing", status: company.purchasingIntegration.status },
	];

	const configured = integrations.filter((i) => i.status !== "not_configured");
	const errors = configured.filter((i) => i.status === "error");
	const notConnected = configured.filter((i) => i.status === "not_connected");
	const connected = configured.filter((i) => i.status === "connected");

	if (configured.length === 0) {
		return (
			<div className="flex items-center gap-2.5 rounded-md bg-muted/50 border px-3 py-2.5 text-sm text-muted-foreground">
				<AlertCircle className="h-4 w-4 shrink-0" />
				<span>No integrations configured yet.</span>
			</div>
		);
	}
	if (errors.length > 0) {
		return (
			<div className="flex items-start gap-2.5 rounded-md bg-destructive/5 border border-destructive/20 px-3 py-2.5">
				<XCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
				<div>
					<p className="text-sm font-medium text-destructive">
						{errors.length} integration{errors.length > 1 ? "s" : ""}{" "}
						{errors.length > 1 ? "have" : "has"} an error
					</p>
					<p className="text-xs text-muted-foreground mt-0.5">
						{errors.map((e) => e.label).join(", ")} — update the configuration
						below
					</p>
				</div>
			</div>
		);
	}
	if (notConnected.length > 0) {
		return (
			<div className="flex items-start gap-2.5 rounded-md bg-warning-50 dark:bg-warning-950/20 border border-warning-200 dark:border-warning-800 px-3 py-2.5">
				<AlertCircle className="h-4 w-4 shrink-0 text-warning-600 mt-0.5" />
				<div>
					<p className="text-sm font-medium text-warning-800 dark:text-warning-200">
						{notConnected.length} integration
						{notConnected.length > 1 ? "s" : ""} not connected
					</p>
					<p className="text-xs text-warning-600 dark:text-warning-400 mt-0.5">
						{notConnected.map((e) => e.label).join(", ")} — use Test Connection
						below to diagnose
					</p>
				</div>
			</div>
		);
	}
	return (
		<div className="flex items-center gap-2.5 rounded-md bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-3 py-2.5">
			<CheckCircle2 className="h-4 w-4 shrink-0 text-success-600" />
			<p className="text-sm font-medium text-success-800 dark:text-success-200">
				{connected.length === configured.length
					? `All ${connected.length} integration${connected.length > 1 ? "s" : ""} connected`
					: `${connected.length} of ${configured.length} integrations connected`}
			</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// CompanyDetailsSection (controlled — no internal state or save button)
// ---------------------------------------------------------------------------

interface CompanyDetailsSectionProps {
	draft: SheetDraft;
	onDraftChange: (
		key: "companyName" | "companyCode" | "isActive",
		value: string | boolean,
	) => void;
}

function CompanyDetailsSection({
	draft,
	onDraftChange,
}: CompanyDetailsSectionProps) {
	return (
		<div className="space-y-4">
			<div className="space-y-3">
				<div className="space-y-1.5">
					<Label htmlFor="company-name">Company Name</Label>
					<Input
						id="company-name"
						value={draft.companyName}
						onChange={(e) => onDraftChange("companyName", e.target.value)}
					/>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="company-code">Company Code</Label>
					<Input
						id="company-code"
						value={draft.companyCode}
						onChange={(e) =>
							onDraftChange("companyCode", e.target.value.toUpperCase())
						}
						placeholder="e.g. ACME"
						className="uppercase"
					/>
				</div>
				<div className="flex items-center justify-between py-1">
					<div>
						<Label htmlFor="company-status" className="text-sm font-medium">
							Active
						</Label>
						<p className="text-xs text-muted-foreground mt-0.5">
							Inactive companies are hidden from the company switcher
						</p>
					</div>
					<Switch
						id="company-status"
						checked={draft.isActive}
						onCheckedChange={(checked) => onDraftChange("isActive", checked)}
					/>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// IntegrationSubSection
// ---------------------------------------------------------------------------

interface IntegrationSubSectionProps {
	companyId: number;
	companyName: string;
	type: IntegrationType;
	config: SimpleIntegrationConfig | undefined | null;
	draft: IntegrationDraft;
	onDraftChange: (type: IntegrationType, draft: IntegrationDraft) => void;
	onSaved: (updated: CompanyWithIntegrations) => void;
	financeProduct?: string | null;
}

const FALLBACK_CONFIG: SimpleIntegrationConfig = {
	product: null,
	databaseId: null,
	status: "not_configured",
	lastSyncAt: null,
};

function IntegrationSubSection({
	companyId,
	companyName,
	type,
	config: rawConfig,
	draft,
	onDraftChange,
	onSaved,
	financeProduct,
}: IntegrationSubSectionProps) {
	const config = rawConfig ?? FALLBACK_CONFIG;
	const isConnected = config.status === "connected";
	const [isTesting, setIsTesting] = useState(false);
	const [testResult, setTestResult] = useState<boolean | null>(null);
	const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const { startFinanceSync, isSyncInFlight } = useSyncNotification();
	const financeSyncActive = type === "finance" && isSyncInFlight(companyId);

	const [isSyncing, setIsSyncing] = useState(financeSyncActive);
	const [isResyncing, setIsResyncing] = useState(false);
	const [showResyncConfirm, setShowResyncConfirm] = useState(false);
	const [availableDbs, setAvailableDbs] = useState<AvailableDatabase[]>([]);
	const [isLoadingDbs, setIsLoadingDbs] = useState(false);
	const [errorModalOpen, setErrorModalOpen] = useState(false);
	const [finOpsModalOpen, setFinOpsModalOpen] = useState(false);
	const isInitialMountRef = useRef(true);

	useEffect(() => {
		setTestResult(null);
	}, [config]);

	// Load databases whenever the selected product changes.
	// For planner products the backend handles the two-step pattern server-side:
	// it queries the Workspace API for instances, then calls the scheduler API
	// to resolve real company names before returning results.
	//
	// Only pass the saved databaseId when the product hasn't been changed by the
	// user — otherwise the old product's database ID leaks into the new product's
	// query and the backend injects it as a raw-GUID fallback entry.
	useEffect(() => {
		if (!draft.product) {
			setAvailableDbs([]);
			return;
		}
		const productMatchesSaved = draft.product === config.product;
		const currentDbId = productMatchesSaved
			? (config.databaseId ?? undefined)
			: undefined;
		let cancelled = false;
		setIsLoadingDbs(true);
		getAvailableDatabases(draft.product, currentDbId, companyId)
			.then((dbs) => {
				if (!cancelled) {
					setAvailableDbs(dbs);
					setIsLoadingDbs(false);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAvailableDbs([]);
					setIsLoadingDbs(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [draft.product, config.product, config.databaseId, companyId]);

	// Finance-gating: Timesheets and Purchasing are gated behind a Finance product selection
	const isFinanceGated =
		(type === "timesheet" || type === "purchasing") && !financeProduct;

	const productOptions = (() => {
		if (type === "finance") return Object.entries(FINANCE_PRODUCT_LABELS);
		if (type === "expense") return Object.entries(EXPENSE_PRODUCT_LABELS);
		if (type === "planner") return Object.entries(PLANNER_PRODUCT_LABELS);
		if (type === "timesheet") {
			return Object.entries(TIMESHEET_PRODUCT_LABELS).filter(
				([value]) => {
					if (value === "access-time") return true;
					if (financeProduct === "access-financials")
						return value === "access-financial-operations";
					if (financeProduct === "access-dimensions")
						return value === "access-focalpoint";
					return false;
				},
			);
		}
		// purchasing
		return Object.entries(PURCHASING_PRODUCT_LABELS).filter(
			([value]) => {
				if (financeProduct === "access-financials")
					return value === "access-financial-operations-purchasing";
				if (financeProduct === "access-dimensions")
					return value === "access-focalpoint-purchasing";
				return false;
			},
		);
	})();

	// Auto-select for Purchasing: when there's exactly one valid product, select it
	const isPurchasingLocked =
		type === "purchasing" && !!financeProduct && productOptions.length === 1;

	useEffect(() => {
		if (isInitialMountRef.current) return;
		if (
			type === "purchasing" &&
			financeProduct &&
			productOptions.length === 1 &&
			!isConnected &&
			draft.product !== productOptions[0][0]
		) {
			onDraftChange(type, { product: productOptions[0][0], databaseId: "" });
		}
	}, [financeProduct, type, isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

	// Auto-select for Expense: single product, auto-select while editable
	useEffect(() => {
		if (isInitialMountRef.current) return;
		if (
			type === "expense" &&
			!isConnected &&
			!draft.product &&
			productOptions.length === 1
		) {
			onDraftChange(type, { product: productOptions[0][0], databaseId: "" });
		}
	}, [type, isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

	// Clear the initial-mount flag after all auto-select effects have executed
	useEffect(() => {
		isInitialMountRef.current = false;
	}, []);

	const label =
		type === "finance"
			? "Finance"
			: type === "timesheet"
				? "Timesheets"
				: type === "expense"
					? "Expense"
					: type === "planner"
						? "Scheduler"
						: "Purchasing";

	const icon =
		type === "finance" ? (
			<PoundSterling className="h-4 w-4 text-muted-foreground flex-shrink-0" />
		) : type === "timesheet" ? (
			<Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
		) : type === "expense" ? (
			<Receipt className="h-4 w-4 text-muted-foreground flex-shrink-0" />
		) : type === "planner" ? (
			<CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
		) : (
			<ShoppingCart className="h-4 w-4 text-muted-foreground flex-shrink-0" />
		);

	async function handleTest() {
		setIsTesting(true);
		setTestResult(null);
		try {
			const result = await testConnection(companyId, type);
			setTestResult(result.success);
			if (result.success) {
				toast.success(result.message || "Connection test successful");
			} else {
				toast.error(result.message || "Connection test failed");
			}
			// Refetch the company so the badge updates
			const { getCompany } = await import(
				"@/services/integrationsRedesignService"
			);
			const refreshed = await getCompany(companyId);
			if (refreshed) {
				onSaved(refreshed);
			}
		} catch {
			setTestResult(false);
			toast.error(`Failed to test ${label.toLowerCase()} connection`);
		} finally {
			setIsTesting(false);
		}
	}

	async function handleSync() {
		if (type === "finance") {
			setIsSyncing(true);
			startFinanceSync({
				companyId,
				companyName,
				fullResync: false,
				onCoreComplete: (updated) => {
					setIsSyncing(false);
					onSaved(updated);
				},
			});
			return;
		}

		setIsSyncing(true);
		try {
			const result = await syncIntegration(companyId, type);
			if (result.success) {
				toast.success("Sync complete", { description: result.message });
				if (result.updatedCompany) {
					onSaved(result.updatedCompany);
				}
			} else {
				toast.error("Sync failed", { description: result.message });
			}
			setIsSyncing(false);
		} catch {
			toast.error(`Failed to sync ${label.toLowerCase()} integration`);
			setIsSyncing(false);
		}
	}

	async function handleResync() {
		setShowResyncConfirm(false);

		if (type === "finance") {
			setIsResyncing(true);
			startFinanceSync({
				companyId,
				companyName,
				fullResync: true,
				onCoreComplete: (updated) => {
					setIsResyncing(false);
					onSaved(updated);
				},
			});
			return;
		}

		setIsResyncing(true);
		try {
			const result = await resyncIntegration(companyId, type);
			if (result.success) {
				toast.success("Full re-sync complete", { description: result.message });
				if (result.updatedCompany) {
					onSaved(result.updatedCompany);
				}
			} else {
				toast.error("Re-sync failed", { description: result.message });
			}
			setIsResyncing(false);
		} catch {
			toast.error(`Failed to re-sync ${label.toLowerCase()} integration`);
			setIsResyncing(false);
		}
	}

	async function handleRemove() {
		setIsRemoving(true);
		try {
			const updated = await removeIntegration(companyId, type);
			if (updated) {
				onSaved(updated);
				onDraftChange(type, buildEmptyIntegrationDraft());
				setAvailableDbs([]);
				setTestResult(null);
				toast.success(`${label} integration removed`);
			}
		} catch {
			toast.error(`Failed to remove ${label.toLowerCase()} integration`);
		} finally {
			setIsRemoving(false);
			setShowRemoveConfirm(false);
		}
	}

	// Derived: has this row's draft diverged from the saved config?
	const isIntegrationDirty =
		draft.product !== (config.product ?? "") ||
		draft.databaseId !== (config.databaseId ?? "");

	// Derived guard: only enable Test Connection when the integration has been persisted and has no unsaved edits
	const canTest =
		!!draft.product &&
		!!draft.databaseId &&
		config.status !== "not_configured" &&
		!isIntegrationDirty;
	const dbLabel =
		type === "planner" ||
		(type === "timesheet" && draft.product === "access-time")
			? "company"
			: type === "purchasing"
				? "instance"
				: "database";
	// Non-finance integrations receive data automatically, so their manual sync is a
	// no-op; finance is never affected because handleSync runs a real startFinanceSync.
	const syncNowDeprecated = NON_FINANCE_SYNC_DEPRECATED && type !== "finance";
	// Dev shortcut only exists while the inline GUID row is hidden.
	const finOpsShortcutEnabled =
		!SHOW_FINOPS_INSTANCE_ID &&
		type === "purchasing" &&
		!!config.finOpsInstanceId;
	const handleConnectedClick = (e: React.MouseEvent) => {
		if (!finOpsShortcutEnabled) return;
		if (!e.ctrlKey || !e.shiftKey) return;
		setFinOpsModalOpen(true);
	};
	const testTooltipMessage = !draft.product
		? "Select a product first"
		: !draft.databaseId
			? `Select a ${dbLabel} first`
			: "Save the integration first to test the connection";

	return (
		<>
			<div className="rounded-lg border bg-card p-4 space-y-3">
				<div className="flex items-center justify-between gap-2">
					<div className="flex items-center gap-2 min-w-0">
						{icon}
						<span className="text-sm font-semibold truncate">
							{label} Integration
						</span>
					</div>
					<ConnectionStatusBadge
						status={config.status}
						errorMessage={config.errorMessage}
						onErrorClick={() => setErrorModalOpen(true)}
						onConnectedClick={
							finOpsShortcutEnabled ? handleConnectedClick : undefined
						}
					/>
				</div>
				<div className="space-y-3">
						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">Product</Label>
							<Select
								value={draft.product}
								onValueChange={(v) => {
									onDraftChange(type, { product: v, databaseId: "" });
								}}
								disabled={isConnected || isFinanceGated || isPurchasingLocked}
							>
								<SelectTrigger className="h-8 text-sm">
									<SelectValue
										placeholder={
											isFinanceGated
												? "Select a Finance product first"
												: "Select product..."
										}
									/>
								</SelectTrigger>
								<SelectContent>
									{productOptions.map(([value, optLabel]) => (
										<SelectItem key={value} value={value}>
											{optLabel}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">
								{type === "planner" ||
								(type === "timesheet" && draft.product === "access-time")
									? "Company"
									: type === "purchasing"
										? "Instance"
										: "Database"}
							</Label>
							<Select
								value={draft.databaseId}
								onValueChange={(v) => {
									onDraftChange(type, { ...draft, databaseId: v });
								}}
								disabled={isConnected || !draft.product || isLoadingDbs}
							>
								<SelectTrigger className="h-8 text-sm">
									<SelectValue
										placeholder={
											!draft.product
												? "Select a product first"
												: isLoadingDbs
													? type === "planner" ||
														(type === "timesheet" &&
															draft.product === "access-time")
														? "Loading companies…"
														: type === "purchasing"
															? "Loading instances…"
															: "Loading databases…"
													: availableDbs.length === 0
														? type === "planner" ||
															(type === "timesheet" &&
																draft.product === "access-time")
															? "No companies available"
															: type === "purchasing"
																? "No instances available"
																: "No databases available"
														: type === "planner" ||
																(type === "timesheet" &&
																	draft.product === "access-time")
															? "Select a company"
															: type === "purchasing"
																? "Select instance…"
																: "Select database…"
										}
									/>
								</SelectTrigger>
								<SelectContent>
									{availableDbs.map((db) => (
										<SelectItem key={db.id} value={db.id}>
											<span className="flex items-center justify-between gap-3 w-full">
												<span>{db.label}</span>
												{db.environment && (
													<span className="text-xs text-muted-foreground shrink-0">
														{db.environment}
													</span>
												)}
											</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{SHOW_FINOPS_INSTANCE_ID &&
							type === "purchasing" &&
							config.finOpsInstanceId && (
							<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
								<CheckCircle2 className="h-3 w-3 text-success-600 flex-shrink-0" />
								<span>
									FinOps instance:{" "}
									<span className="font-medium text-foreground">
										{config.finOpsInstanceId}
									</span>
								</span>
							</div>
						)}
						{config.lastSyncAt && (
							<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
								<RefreshCw className="h-3 w-3 flex-shrink-0" />
								<span>
									{syncNowDeprecated ? "Last verified:" : "Last synced:"}{" "}
									<span className="font-medium text-foreground">
										{formatLastSync(config.lastSyncAt)}
									</span>
								</span>
							</div>
						)}
						{config.lastFullSyncAt && (
							<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
								<RotateCcw className="h-3 w-3 flex-shrink-0" />
								<span>
									Last full sync:{" "}
									<span className="font-medium text-foreground">
										{formatLastSync(config.lastFullSyncAt)}
									</span>
								</span>
							</div>
						)}
						<div className="flex flex-wrap gap-2 pt-1 pb-2">
							{config.status === "connected" && (
								<TooltipProvider delayDuration={300}>
									<Tooltip>
										<TooltipTrigger asChild>
											<span
												tabIndex={syncNowDeprecated ? 0 : -1}
												className="inline-flex"
											>
												<div className="flex items-stretch gap-0">
													<Button
														variant="outline"
														size="sm"
														className="text-xs h-7 rounded-r-none border-r-0"
														onClick={handleSync}
														disabled={isSyncing || isResyncing || syncNowDeprecated}
													>
														{isSyncing ? (
															<Loader2 className="h-3 w-3 mr-1 animate-spin" />
														) : (
															<RefreshCw className="h-3 w-3 mr-1" />
														)}
														{isSyncing ? "Syncing…" : "Sync Now"}
													</Button>
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button
																variant="outline"
																size="sm"
																className="text-xs h-7 rounded-l-none px-1.5"
																disabled={
																	isSyncing || isResyncing || syncNowDeprecated
																}
															>
																<ChevronDown className="h-3 w-3" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end" className="w-44">
															<DropdownMenuItem
																className="text-xs gap-2 text-muted-foreground"
																onClick={() => setShowResyncConfirm(true)}
															>
																<RotateCcw className="h-3.5 w-3.5" />
																Full Re-sync…
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</div>
											</span>
										</TooltipTrigger>
										{syncNowDeprecated && (
											<TooltipContent
												side="bottom"
												className="text-xs max-w-56 text-center"
											>
												{SYNC_DEPRECATED_TOOLTIP}
											</TooltipContent>
										)}
									</Tooltip>
								</TooltipProvider>
							)}
							<TooltipProvider delayDuration={300}>
								<Tooltip>
									<TooltipTrigger asChild>
										<span tabIndex={!canTest ? 0 : -1}>
											<Button
												variant="outline"
												size="sm"
												className="text-xs h-7"
												onClick={handleTest}
												disabled={isTesting || !canTest}
											>
												{isTesting ? (
													<Loader2 className="h-3 w-3 mr-1 animate-spin" />
												) : (
													<CheckCircle2 className="h-3 w-3 mr-1" />
												)}
												Test Connection
											</Button>
										</span>
									</TooltipTrigger>
									{!canTest && (
										<TooltipContent
											side="bottom"
											className="text-xs max-w-48 text-center"
										>
											{testTooltipMessage}
										</TooltipContent>
									)}
								</Tooltip>
							</TooltipProvider>
							{testResult !== null && (
								<span
									className={cn(
										"text-xs font-medium flex items-center gap-1",
										testResult ? "text-success-600" : "text-destructive",
									)}
								>
									{testResult ? (
										<CheckCircle2 className="h-3 w-3" />
									) : (
										<XCircle className="h-3 w-3" />
									)}
									{testResult ? "Pass" : "Failed"}
								</span>
							)}
							{config.status !== "not_configured" && (
								<Button
									variant="ghost"
									size="sm"
									className="text-xs h-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
									onClick={() => setShowRemoveConfirm(true)}
									disabled={isRemoving}
								>
									{isRemoving ? (
										<Loader2 className="h-3 w-3 mr-1 animate-spin" />
									) : (
										<Trash2 className="h-3 w-3 mr-1" />
									)}
									Remove
								</Button>
							)}
						</div>
				</div>
			</div>

			<AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove {label} integration?</AlertDialogTitle>
						<AlertDialogDescription>
							This will disconnect and remove all configuration for{" "}
							{label.toLowerCase()}. Any active sync schedule will be cancelled.
							This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleRemove}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Remove Integration
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={showResyncConfirm} onOpenChange={setShowResyncConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle className="flex items-center gap-2">
							<AlertTriangle className="h-4 w-4 text-warning-600" />
							Full Re-sync {label} Integration?
						</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-2 text-sm text-muted-foreground">
								<p>
									A full re-sync rebuilds all data from scratch. This is
									different from a standard sync which only picks up recent
									changes.
								</p>
								<ul className="list-disc pl-4 space-y-1 text-xs">
									<li>May take several minutes to complete</li>
									<li>Places higher load on the connected database</li>
									<li>
										All incremental changes since the last sync will be
										reprocessed
									</li>
								</ul>
								<p className="text-xs font-medium text-foreground">
									Only use this if you suspect data is out of sync. For routine
									updates, use Sync Now instead.
								</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleResync}
							className="bg-warning-600 hover:bg-warning-700 text-white"
						>
							{isResyncing ? (
								<>
									<Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
									Re-syncing…
								</>
							) : (
								"Start Full Re-sync"
							)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertCircle className="h-4 w-4 text-destructive" />
							{label} integration error
						</DialogTitle>
						<DialogDescription>
							The last sync or connection attempt for this integration failed.
						</DialogDescription>
					</DialogHeader>
					<div className="rounded-md border bg-muted/50 p-3 font-mono text-xs whitespace-pre-wrap break-words max-h-64 overflow-auto">
						{config.errorMessage ??
							"Connection failed — check the integration configuration."}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setErrorModalOpen(false)}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={finOpsModalOpen} onOpenChange={setFinOpsModalOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>FinOps Instance (developer)</DialogTitle>
						<DialogDescription>
							Diagnostic identifier for the connected FinOps instance.
						</DialogDescription>
					</DialogHeader>
					<div className="rounded-md border bg-muted/50 p-3 font-mono text-xs break-all">
						{config.finOpsInstanceId ?? "Not available"}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setFinOpsModalOpen(false)}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

// ---------------------------------------------------------------------------
// AddUsersDialog (integrations — server-side search)
// ---------------------------------------------------------------------------

interface AddUsersDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	companyName: string;
	searchQuery: string;
	onSearchChange: (q: string) => void;
	availableUsers: CompanyUser[];
	isLoadingUsers: boolean;
	onUsersAdded: (ids: string[]) => Promise<void>;
}

function AddUsersDialog({
	open,
	onOpenChange,
	companyName,
	searchQuery,
	onSearchChange,
	availableUsers,
	isLoadingUsers,
	onUsersAdded,
}: AddUsersDialogProps) {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [isAdding, setIsAdding] = useState(false);

	useEffect(() => {
		if (!open) return;
		setSelectedIds(new Set());
	}, [open]);

	const allSelected =
		availableUsers.length > 0 &&
		availableUsers.every((u) => selectedIds.has(u.id));
	const someSelected =
		availableUsers.some((u) => selectedIds.has(u.id)) && !allSelected;

	function toggleUser(id: string) {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	function toggleAll() {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				availableUsers.forEach((u) => next.delete(u.id));
			} else {
				availableUsers.forEach((u) => next.add(u.id));
			}
			return next;
		});
	}

	async function handleConfirm() {
		if (selectedIds.size === 0) return;
		setIsAdding(true);
		try {
			await onUsersAdded(Array.from(selectedIds));
			onOpenChange(false);
		} catch {
			toast.error("Failed to add some users");
		} finally {
			setIsAdding(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
				<DialogHeader className="px-5 pt-5 pb-4 border-b shrink-0">
					<DialogTitle className="flex items-center gap-2 text-base">
						<UserPlus className="h-4 w-4 text-muted-foreground" />
						Add Users to {companyName}
					</DialogTitle>
					<DialogDescription className="text-xs mt-1">
						Select one or more users to grant access to this company.
					</DialogDescription>
				</DialogHeader>

				{/* Search */}
				<div className="px-4 py-3 border-b shrink-0">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
						<input
							className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 pl-8 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							placeholder="Search by name, email, role…"
							value={searchQuery}
							onChange={(e) => onSearchChange(e.target.value)}
							autoFocus
						/>
					</div>
				</div>

				{/* Select all row */}
				{!isLoadingUsers && availableUsers.length > 0 && (
					<div
						className="flex items-center gap-3 px-4 py-2 bg-muted/40 border-b shrink-0 cursor-pointer hover:bg-muted/60 transition-colors"
						onClick={toggleAll}
					>
						<Checkbox
							checked={allSelected}
							data-state={someSelected ? "indeterminate" : undefined}
							onCheckedChange={toggleAll}
							className="pointer-events-none"
						/>
						<span className="text-xs font-medium text-muted-foreground">
							{allSelected
								? "Deselect all"
								: someSelected
									? `${selectedIds.size} selected — select all ${availableUsers.length}`
									: `Select all ${availableUsers.length} user${availableUsers.length !== 1 ? "s" : ""}`}
						</span>
					</div>
				)}

				{/* User list */}
				<div className="flex-1 overflow-y-auto min-h-0">
					{isLoadingUsers ? (
						<div className="space-y-2 p-4">
							{[1, 2, 3, 4].map((i) => (
								<div key={i} className="flex items-center gap-3 py-1">
									<Skeleton className="h-4 w-4 rounded" />
									<Skeleton className="h-7 w-7 rounded-full" />
									<div className="space-y-1 flex-1">
										<Skeleton className="h-3 w-32" />
										<Skeleton className="h-3 w-20" />
									</div>
								</div>
							))}
						</div>
					) : availableUsers.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-10 text-center gap-2 px-4">
							{searchQuery ? (
								<>
									<Search className="h-7 w-7 text-muted-foreground/40" />
									<p className="text-sm text-muted-foreground">
										No users match "{searchQuery}"
									</p>
								</>
							) : (
								<>
									<Users className="h-7 w-7 text-muted-foreground/40" />
									<p className="text-sm text-muted-foreground">
										All users already have access
									</p>
								</>
							)}
						</div>
					) : (
						<div className="divide-y">
							{availableUsers.map((user) => {
								const isSelected = selectedIds.has(user.id);
								return (
									<div
										key={user.id}
										className={cn(
											"flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors",
											isSelected ? "bg-primary/5" : "hover:bg-muted/50",
										)}
										onClick={() => toggleUser(user.id)}
									>
										<Checkbox
											checked={isSelected}
											onCheckedChange={() => toggleUser(user.id)}
											className="pointer-events-none shrink-0"
										/>
										<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
											{user.name
												.split(" ")
												.map((n) => n[0])
												.join("")
												.slice(0, 2)
												.toUpperCase()}
										</div>
										<div className="min-w-0 flex-1">
											<div className="text-sm font-medium leading-tight truncate">
												{user.name}
											</div>
											<div className="text-xs text-muted-foreground truncate">
												{user.role}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Footer */}
				<DialogFooter className="px-4 py-3 border-t shrink-0 gap-2 sm:gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => onOpenChange(false)}
						disabled={isAdding}
					>
						Cancel
					</Button>
					<Button
						size="sm"
						onClick={handleConfirm}
						disabled={selectedIds.size === 0 || isAdding}
						className="gap-1.5"
					>
						{isAdding ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<UserPlus className="h-3.5 w-3.5" />
						)}
						{selectedIds.size > 0
							? `Add ${selectedIds.size} User${selectedIds.size !== 1 ? "s" : ""}`
							: "Add Users"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// BulkRemoveBar (integrations)
// ---------------------------------------------------------------------------

function BulkRemoveBar({
	selectedCount,
	onRemove,
	onClear,
	isRemoving,
}: {
	selectedCount: number;
	onRemove: () => void;
	onClear: () => void;
	isRemoving: boolean;
}) {
	if (selectedCount === 0) return null;
	return (
		<div className="sticky bottom-0 left-0 right-0 z-10 pointer-events-none">
			<div className="flex justify-center pb-2">
				<div className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground shadow-2xl text-sm whitespace-nowrap animate-in slide-in-from-bottom-4 duration-300">
					<span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold px-1">
						{selectedCount}
					</span>
					<span className="font-medium pr-1">
						{selectedCount === 1 ? "user" : "users"} selected
					</span>
					<div className="h-4 w-px bg-white/30" />
					<button
						onClick={onRemove}
						disabled={isRemoving}
						className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors font-medium disabled:opacity-60"
					>
						{isRemoving ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<Trash2 className="h-3.5 w-3.5" />
						)}
						Remove Access
					</button>
					<div className="h-4 w-px bg-white/30" />
					<button
						onClick={onClear}
						className="flex items-center gap-1 text-white/80 hover:text-white transition-colors px-1"
						aria-label="Clear selection"
					>
						<X className="h-3.5 w-3.5" />
						<span className="text-xs">Clear</span>
					</button>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// UsersWithAccessSection
// ---------------------------------------------------------------------------

interface UsersWithAccessSectionProps {
	company: CompanyWithIntegrations;
	onUserCountChange: (count: number) => void;
}

function UsersWithAccessSection({
	company,
	onUserCountChange,
}: UsersWithAccessSectionProps) {
	const [users, setUsers] = useState<CompanyUser[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [filterQuery, setFilterQuery] = useState("");
	const [searchQuery, setSearchQuery] = useState("");
	const [allAvailableUsers, setAllAvailableUsers] = useState<CompanyUser[]>([]);
	const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
	const [isAddOpen, setIsAddOpen] = useState(false);
	const [removingUser, setRemovingUser] = useState<CompanyUser | null>(null);
	const [isRemoving, setIsRemoving] = useState(false);
	const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(
		new Set(),
	);
	const [showBulkRemoveConfirm, setShowBulkRemoveConfirm] = useState(false);
	const [isBulkRemoving, setIsBulkRemoving] = useState(false);

	const loadUsers = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getCompanyUsers(company.id);
			setUsers(data);
			onUserCountChange(data.length);
		} catch {
			setUsers([]);
			onUserCountChange(0);
		} finally {
			setIsLoading(false);
		}
	}, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		loadUsers();
	}, [loadUsers]);

	// Server-side search: fetch users from the API whenever the popover
	// is open and the search query changes (debounced).
	useEffect(() => {
		if (!isAddOpen) {
			setAllAvailableUsers([]);
			setSearchQuery("");
			return;
		}
		let cancelled = false;
		setIsLoadingAvailable(true);
		const debounce = setTimeout(
			() => {
				searchUsers(searchQuery, company.id)
					.then((results) => {
						if (!cancelled) setAllAvailableUsers(results);
					})
					.catch(() => {
						if (!cancelled) setAllAvailableUsers([]);
					})
					.finally(() => {
						if (!cancelled) setIsLoadingAvailable(false);
					});
			},
			searchQuery ? 300 : 0,
		);
		return () => {
			cancelled = true;
			clearTimeout(debounce);
		};
	}, [isAddOpen, company.id, searchQuery]);

	const filteredUsers = filterQuery.trim()
		? users.filter(
				(u) =>
					u.name.toLowerCase().includes(filterQuery.toLowerCase()) ||
					u.role.toLowerCase().includes(filterQuery.toLowerCase()),
			)
		: users;

	const allBulkSelected =
		filteredUsers.length > 0 &&
		filteredUsers.every((u) => bulkSelectedIds.has(u.id));
	const someBulkSelected =
		filteredUsers.some((u) => bulkSelectedIds.has(u.id)) && !allBulkSelected;

	function toggleBulkUser(id: string) {
		setBulkSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	function toggleAllBulk() {
		setBulkSelectedIds((prev) => {
			const next = new Set(prev);
			if (allBulkSelected) {
				filteredUsers.forEach((u) => next.delete(u.id));
			} else {
				filteredUsers.forEach((u) => next.add(u.id));
			}
			return next;
		});
	}

	async function handleUsersAdded(ids: string[]) {
		await Promise.all(ids.map((id) => addUserToCompany(company.id, id)));
		await loadUsers();
		setAllAvailableUsers((prev) => prev.filter((u) => !ids.includes(u.id)));
		setSearchQuery("");
		toast.success(
			`${ids.length} user${ids.length !== 1 ? "s" : ""} added to ${company.name}`,
		);
	}

	async function confirmBulkRemove() {
		setIsBulkRemoving(true);
		const ids = Array.from(bulkSelectedIds);
		try {
			await Promise.all(ids.map((id) => removeUserFromCompany(company.id, id)));
			await loadUsers();
			setBulkSelectedIds(new Set());
			toast.success(
				`${ids.length} user${ids.length !== 1 ? "s" : ""} removed from ${company.name}`,
			);
		} catch {
			toast.error("Failed to remove some users");
		} finally {
			setIsBulkRemoving(false);
			setShowBulkRemoveConfirm(false);
		}
	}

	return (
		<div className="space-y-3">
			{/* Add Users button */}
			<div className="flex items-center justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-8 text-xs gap-1 shrink-0"
					onClick={() => setIsAddOpen(true)}
				>
					<UserPlus className="h-3.5 w-3.5" />
					Add Users
				</Button>
			</div>

			{/* Count line */}
			{!isLoading && (
				<p className="text-xs text-muted-foreground">
					{filterQuery.trim()
						? `${filteredUsers.length} of ${users.length} user${users.length !== 1 ? "s" : ""} match`
						: `${users.length} user${users.length !== 1 ? "s" : ""} with access`}
				</p>
			)}

			{/* Loading skeletons */}
			{isLoading ? (
				<div className="space-y-2">
					{[1, 2, 3].map((i) => (
						<div key={i} className="flex items-center gap-2 py-1">
							<Skeleton className="h-8 w-8 rounded-full" />
							<div className="space-y-1 flex-1">
								<Skeleton className="h-3 w-28" />
								<Skeleton className="h-3 w-16" />
							</div>
						</div>
					))}
				</div>
			) : users.length === 0 ? (
				/* Empty state */
				<div className="flex flex-col items-center justify-center py-10 text-center gap-2">
					<UserRound className="h-8 w-8 text-muted-foreground/40" />
					<p className="text-sm text-muted-foreground">
						No users assigned yet.
					</p>
					<p className="text-xs text-muted-foreground">
						Use "Add User" to grant access to this company.
					</p>
				</div>
			) : (
				/* User list with bulk-select */
				<div className="rounded-lg border border-border overflow-hidden">
					{/* Select all header */}
					<div
						className="flex items-center gap-2.5 px-3 py-2 bg-muted/40 border-b cursor-pointer hover:bg-muted/60 transition-colors"
						onClick={toggleAllBulk}
					>
						<Checkbox
							checked={allBulkSelected}
							data-state={someBulkSelected ? "indeterminate" : undefined}
							onCheckedChange={toggleAllBulk}
							className="pointer-events-none shrink-0"
						/>
						<span className="text-xs font-medium text-muted-foreground">
							{allBulkSelected
								? "Deselect all"
								: someBulkSelected
									? `${bulkSelectedIds.size} selected — select all ${filteredUsers.length}`
									: `Select all ${filteredUsers.length} user${filteredUsers.length !== 1 ? "s" : ""}`}
						</span>
					</div>
					<div className="divide-y">
						{filteredUsers.map((user) => {
							const isSelected = bulkSelectedIds.has(user.id);
							return (
								<div
									key={user.id}
									className={cn(
										"flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors group",
										isSelected ? "bg-primary/5" : "hover:bg-muted/50",
									)}
									onClick={() => toggleBulkUser(user.id)}
								>
									<Checkbox
										checked={isSelected}
										onCheckedChange={() => toggleBulkUser(user.id)}
										className="pointer-events-none shrink-0"
									/>
									<div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">
										{user.name
											.split(" ")
											.map((w) => w[0])
											.slice(0, 2)
											.join("")
											.toUpperCase()}
									</div>
									<div className="min-w-0 flex-1">
										<div className="text-sm font-medium leading-tight truncate">
											{user.name}
										</div>
										<div className="text-xs text-muted-foreground truncate">
											{user.role}
										</div>
									</div>
									<button
										className={cn(
											"opacity-0 group-hover:opacity-100 transition-opacity",
											"h-7 w-7 flex items-center justify-center rounded-md shrink-0",
											"text-muted-foreground hover:text-destructive hover:bg-destructive/10",
										)}
										onClick={(e) => {
											e.stopPropagation();
											setRemovingUser(user);
										}}
										title={`Remove ${user.name}`}
									>
										<X className="h-3.5 w-3.5" />
									</button>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* Bulk remove bar */}
			<BulkRemoveBar
				selectedCount={bulkSelectedIds.size}
				onRemove={() => setShowBulkRemoveConfirm(true)}
				onClear={() => setBulkSelectedIds(new Set())}
				isRemoving={isBulkRemoving}
			/>

			{/* Footer note */}
			<p className="text-xs text-muted-foreground pt-2 border-t">
				To manage a user's full company access, go to{" "}
				<span className="font-medium">Team Members</span>.
			</p>

			{/* Remove user confirm */}
			<AlertDialog
				open={!!removingUser}
				onOpenChange={(o) => {
					if (!o) setRemovingUser(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove access?</AlertDialogTitle>
						<AlertDialogDescription>
							<strong>{removingUser?.name}</strong> will lose access to{" "}
							<strong>{company.name}</strong>. They will no longer see this
							company in their switcher.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							disabled={isRemoving}
							onClick={async () => {
								setIsRemoving(true);
								try {
									await removeUserFromCompany(company.id, removingUser!.id);
									await loadUsers();
									toast.success(
										`${removingUser!.name} removed from ${company.name}`,
									);
								} catch {
									toast.error(`Failed to remove ${removingUser?.name}`);
								} finally {
									setIsRemoving(false);
									setRemovingUser(null);
								}
							}}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{isRemoving ? (
								<Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
							) : null}
							Remove Access
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Bulk remove confirm */}
			<AlertDialog
				open={showBulkRemoveConfirm}
				onOpenChange={setShowBulkRemoveConfirm}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Remove {bulkSelectedIds.size} user
							{bulkSelectedIds.size !== 1 ? "s" : ""}?
						</AlertDialogTitle>
						<AlertDialogDescription>
							These users will lose access to <strong>{company.name}</strong>{" "}
							and will no longer see it in their company switcher.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isBulkRemoving}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={isBulkRemoving}
							onClick={confirmBulkRemove}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{isBulkRemoving ? (
								<Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
							) : null}
							Remove Access
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Add users dialog */}
			<AddUsersDialog
				open={isAddOpen}
				onOpenChange={setIsAddOpen}
				companyName={company.name}
				searchQuery={searchQuery}
				onSearchChange={setSearchQuery}
				availableUsers={allAvailableUsers}
				isLoadingUsers={isLoadingAvailable}
				onUsersAdded={handleUsersAdded}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// CreateCompanyForm
// ---------------------------------------------------------------------------

interface CreateCompanyFormProps {
	existingCodes: string[];
	onCreated: (company: CompanyWithIntegrations) => void;
	onCancel: () => void;
}

function CreateCompanyForm({
	existingCodes,
	onCreated,
	onCancel,
}: CreateCompanyFormProps) {
	const [name, setName] = useState("");
	const [code, setCode] = useState("");
	const [status, setStatus] = useState<"active" | "inactive">("active");
	const [isSaving, setIsSaving] = useState(false);
	const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

	function validate() {
		const e: { name?: string; code?: string } = {};
		if (!name.trim() || name.trim().length < 2)
			e.name = "Company name must be at least 2 characters";
		if (!code.trim() || code.trim().length < 2)
			e.code = "Company code must be at least 2 characters";
		else if (code.trim().length > 20)
			e.code = "Company code must be at most 20 characters";
		else if (!/^[A-Z0-9-]+$/.test(code.trim()))
			e.code = "Uppercase letters, numbers, and hyphens only";
		else if (
			existingCodes
				.map((c) => c.toUpperCase())
				.includes(code.trim().toUpperCase())
		)
			e.code = "This company code is already in use";
		return e;
	}

	async function handleSave() {
		const e = validate();
		if (Object.keys(e).length) {
			setErrors(e);
			return;
		}
		setIsSaving(true);
		try {
			const created = await createCompany({
				name: name.trim(),
				code: code.trim().toUpperCase(),
				status,
			});
			toast.success(`${created.name} created`);
			onCreated(created);
		} catch {
			toast.error("Failed to create company");
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<div className="px-6 py-5 space-y-5">
			<div className="space-y-1.5">
				<Label htmlFor="new-company-name">
					Company Name <span className="text-destructive">*</span>
				</Label>
				<Input
					id="new-company-name"
					value={name}
					placeholder="e.g., UK Operations"
					onChange={(e) => {
						setName(e.target.value);
						setErrors((prev) => ({ ...prev, name: undefined }));
					}}
					disabled={isSaving}
				/>
				{errors.name && (
					<p className="text-xs text-destructive">{errors.name}</p>
				)}
			</div>

			<div className="space-y-1.5">
				<Label htmlFor="new-company-code">
					Company Code <span className="text-destructive">*</span>
				</Label>
				<Input
					id="new-company-code"
					value={code}
					placeholder="e.g., UK-OPS"
					className="uppercase"
					onChange={(e) => {
						setCode(e.target.value.toUpperCase());
						setErrors((prev) => ({ ...prev, code: undefined }));
					}}
					disabled={isSaving}
				/>
				<p className="text-xs text-muted-foreground">
					Unique identifier — uppercase letters, numbers and hyphens only
				</p>
				{errors.code && (
					<p className="text-xs text-destructive">{errors.code}</p>
				)}
			</div>

			<div className="flex items-center justify-between py-1">
				<div>
					<Label htmlFor="new-company-status" className="text-sm font-medium">
						Active
					</Label>
					<p className="text-xs text-muted-foreground mt-0.5">
						Inactive companies are hidden from the company switcher
					</p>
				</div>
				<Switch
					id="new-company-status"
					checked={status === "active"}
					onCheckedChange={(checked) =>
						setStatus(checked ? "active" : "inactive")
					}
					disabled={isSaving}
				/>
			</div>

			<Separator />

			<div className="flex gap-2 pt-1">
				<Button
					variant="outline"
					className="flex-1"
					onClick={onCancel}
					disabled={isSaving}
				>
					Cancel
				</Button>
				<Button className="flex-1" onClick={handleSave} disabled={isSaving}>
					{isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
					Create Company
				</Button>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// CompanySheet — main export
// ---------------------------------------------------------------------------

export function CompanySheet({
	company,
	open,
	onOpenChange,
	onCompanyUpdated,
	onCompanyCreated,
	existingCodes = [],
}: CompanySheetProps) {
	const syncLogRef = useRef<SyncLogSectionHandle>(null);
	const [activeTab, setActiveTab] = useState("integrations");
	const [syncLogPage, setSyncLogPage] = useState(1);
	const [syncLogPageInfo, setSyncLogPageInfo] = useState<SyncLogPageInfo>({
		totalCount: 0,
		totalPages: 0,
		loading: true,
	});

	// Lifted state
	const [draft, setDraft] = useState<SheetDraft | null>(null);
	const [isDirty, setIsDirty] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [showDiscardDialog, setShowDiscardDialog] = useState(false);
	const [activeCompany, setActiveCompany] =
		useState<CompanyWithIntegrations | null>(company);

	// Initialise / reset draft when company or open state changes
	useEffect(() => {
		if (open && company) {
			setActiveCompany(company);
			setIsDirty(false);
			setDraft(buildDraftFromCompany(company));
			setSyncLogPage(1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, company?.id]);

	// Keep activeCompany in sync when parent updates it
	useEffect(() => {
		if (company) setActiveCompany(company);
	}, [company]);

	// Sync Log is unavailable while there are unsaved changes
	function handleTabChange(value: string) {
		if (value === "sync-log" && isDirty) return;
		setActiveTab(value);
	}

	useEffect(() => {
		if (isDirty && activeTab === "sync-log") setActiveTab("integrations");
	}, [isDirty, activeTab]);

	// Intercept sheet close — show discard dialog if dirty
	function handleOpenChange(nextOpen: boolean) {
		if (!nextOpen && isDirty) {
			setShowDiscardDialog(true);
			return;
		}
		onOpenChange(nextOpen);
	}

	function handleDiscard() {
		setShowDiscardDialog(false);
		setIsDirty(false);
		onOpenChange(false);
	}

	function updateDraftField(
		key: "companyName" | "companyCode" | "isActive",
		value: string | boolean,
	) {
		setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
		setIsDirty(true);
	}

	function handleIntegrationDraftChange(
		type: IntegrationType,
		newDraft: IntegrationDraft,
	) {
		setDraft((prev) => {
			if (!prev) return prev;
			const next = { ...prev, [type]: newDraft };

			// Reset dependent drafts when finance product changes
			if (type === "finance" && newDraft.product !== prev.finance.product) {
				const newFinance = newDraft.product;
				// Check if current timesheet product is still valid
				const timesheetConnected =
					activeCompany?.timesheetIntegration?.status === "connected";
				if (!timesheetConnected) {
					const ts = prev.timesheet.product;
					const tsValid =
						ts === "access-time" ||
						(newFinance === "access-financials" &&
							ts === "access-financial-operations") ||
						(newFinance === "access-dimensions" &&
							ts === "access-focalpoint");
					if (!tsValid) {
						next.timesheet = { product: "", databaseId: "" };
					}
				}
				// Check if current purchasing product is still valid
				const purchasingConnected =
					activeCompany?.purchasingIntegration?.status === "connected";
				if (!purchasingConnected) {
					const pu = prev.purchasing.product;
					const puValid =
						(newFinance === "access-financials" &&
							pu === "access-financial-operations-purchasing") ||
						(newFinance === "access-dimensions" &&
							pu === "access-focalpoint-purchasing");
					if (!puValid) {
						next.purchasing = { product: "", databaseId: "" };
					}
				}
			}

			return next;
		});
		setIsDirty(true);
	}

	// Handle live updates from test/sync/remove within rows (no dirty state change)
	function handleRowSaved(updated: CompanyWithIntegrations) {
		setActiveCompany(updated);
		onCompanyUpdated(updated);
		if (!isDirty) {
			setDraft(buildDraftFromCompany(updated));
		}
	}

	function handleUserCountChange(count: number) {
		if (!activeCompany) return;
		const updated = { ...activeCompany, userCount: count };
		setActiveCompany(updated);
		onCompanyUpdated(updated);
	}

	// Single save — commits all changes at once
	async function handleSaveAll() {
		if (!draft || !activeCompany) return;
		setIsSaving(true);
		try {
			// 1. Save company details if changed
			const detailsChanged =
				draft.companyName !== activeCompany.name ||
				draft.companyCode !== activeCompany.code ||
				(draft.isActive ? "active" : "inactive") !== activeCompany.status;

			if (detailsChanged) {
				const updatedCompany = await updateCompany(activeCompany.id, {
					name: draft.companyName,
					code: draft.companyCode.toUpperCase(),
					status: draft.isActive ? "active" : "inactive",
				});
				if (updatedCompany) {
					setActiveCompany(updatedCompany);
					onCompanyUpdated(updatedCompany);
				}
			}

			// 2. Resolve FinOps instance for purchasing if needed
			let finOpsInstanceId: string | null = null;
			if (
				draft.purchasing.product === "access-financial-operations-purchasing" &&
				draft.purchasing.databaseId
			) {
				const dbs = await getAvailableDatabases(
					draft.purchasing.product,
					draft.purchasing.databaseId,
					activeCompany.id,
				);
				const selectedDb = dbs.find(
					(db) => db.id === draft.purchasing.databaseId,
				);
				if (selectedDb) {
					const finOpsInstance = await getFinOpsInstanceByName(
						selectedDb.label,
					);
					if (finOpsInstance) {
						finOpsInstanceId = finOpsInstance.id;
					} else {
						toast.warning(
							`No matching Financials Operations instance found for "${selectedDb.label}"`,
						);
					}
				}
			}

			// 3. Resolve finance database name from available databases
			let financeDatabaseName: string | null = null;
			if (draft.finance.product && draft.finance.databaseId) {
				try {
					const financeDbs = await getAvailableDatabases(
						draft.finance.product,
						draft.finance.databaseId,
						activeCompany.id,
					);
					const selectedFinanceDb = financeDbs.find(
						(db) => db.id === draft.finance.databaseId,
					);
					financeDatabaseName = selectedFinanceDb?.databaseName ?? null;
				} catch {
					// Non-critical — server-side fallback will resolve it
				}
			}

			// 4. Save all integrations
			const toProduct = <T,>(v: string): T => (v || null) as T;
			const payload: UpdateIntegrationsPayload = {
				finance: {
					product: toProduct(draft.finance.product),
					databaseId: draft.finance.databaseId || null,
					databaseName: financeDatabaseName,
				},
				timesheet: {
					product: toProduct(draft.timesheet.product),
					databaseId: draft.timesheet.databaseId || null,
				},
				expense: {
					product: toProduct(draft.expense.product),
					databaseId: draft.expense.databaseId || null,
				},
				planner: {
					product: toProduct(draft.planner.product),
					databaseId: draft.planner.databaseId || null,
				},
				purchasing: {
					product: toProduct(draft.purchasing.product),
					databaseId: draft.purchasing.databaseId || null,
					finOpsInstanceId,
				},
			};

			const updated = await updateIntegrations(activeCompany.id, payload);
			if (updated) {
				setActiveCompany(updated);
				onCompanyUpdated(updated);
				setDraft(buildDraftFromCompany(updated));
				setIsDirty(false);
				toast.success("Changes saved");
			}
		} catch (err) {
			const appError = err as {
				statusCode?: number;
				apiError?: { detail?: string };
			};
			if (appError.statusCode === 409) {
				toast.error("Instance already in use", {
					description:
						appError.apiError?.detail ??
						"One or more instances are reserved by another company",
				});
			} else {
				toast.error("Failed to save changes — please try again");
			}
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<>
			<Sheet open={open} onOpenChange={handleOpenChange}>
				<SheetContent
					side="right"
					className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden"
				>
					{/* Header */}
					<SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
						<div className="flex items-center gap-3">
							<div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
								<Building2 className="h-5 w-5 text-muted-foreground" />
							</div>
							<div className="min-w-0">
								<SheetTitle className="text-base leading-tight truncate">
									{!company
										? "Add New Company"
										: (activeCompany?.name ?? "Company Details")}
								</SheetTitle>
								<SheetDescription className="text-xs">
									{!company
										? "Fill in the details below to create a new company"
										: activeCompany?.code}
								</SheetDescription>
							</div>
						</div>
					</SheetHeader>

					{/* Body */}
					{!company ? (
						<CreateCompanyForm
							existingCodes={existingCodes}
							onCreated={(created) => {
								onCompanyCreated(created);
								onOpenChange(false);
							}}
							onCancel={() => onOpenChange(false)}
						/>
					) : !activeCompany || !draft ? null : (
						<Tabs
							value={activeTab}
							onValueChange={handleTabChange}
							className="flex flex-col flex-1 min-h-0"
						>
							{/* Tab bar */}
							<div className="px-6 pt-3 pb-0 border-b shrink-0">
								<TabsList className="h-9 w-full rounded-none bg-transparent p-0 gap-0 justify-start">
									<TabsTrigger
										value="integrations"
										className="rounded-none border-x-0 border-t-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
									>
										Integrations
									</TabsTrigger>
									<TabsTrigger
										value="details"
										className="rounded-none border-x-0 border-t-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
									>
										Details
									</TabsTrigger>
									<TabsTrigger
										value="users"
										className="rounded-none border-x-0 border-t-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
									>
										Users
										<span
											className={cn(
												"ml-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-medium h-4 min-w-4 px-1",
												activeCompany.userCount === 0
													? "bg-muted/60 text-muted-foreground/60"
													: "bg-muted text-muted-foreground",
											)}
										>
											{activeCompany.userCount}
										</span>
									</TabsTrigger>
									<TooltipProvider>
										<Tooltip open={isDirty ? undefined : false}>
											<TooltipTrigger asChild>
												<span className="inline-flex">
													<TabsTrigger
														value="sync-log"
														disabled={isDirty}
														className="rounded-none border-x-0 border-t-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
													>
														Sync Log
													</TabsTrigger>
												</span>
											</TooltipTrigger>
											<TooltipContent>
												Save or discard your changes before viewing the Sync
												Log.
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</TabsList>
							</div>

							{/* Dirty state warning banner */}
							{isDirty && (
								<div className="flex items-center gap-2 px-6 py-2 bg-warning-50 dark:bg-warning-950/30 border-b border-warning-200 dark:border-warning-800 shrink-0">
									<AlertTriangle className="h-3.5 w-3.5 text-warning-600 shrink-0" />
									<p className="text-xs text-warning-700 dark:text-warning-300 flex-1">
										You have unsaved changes. Use{" "}
										<strong>Save All Changes</strong> below to commit them.
									</p>
								</div>
							)}

							{/* Tab 1: Integrations */}
							<TabsContent
								value="integrations"
								className="flex-1 overflow-y-auto mt-0"
							>
								<div className="px-6 py-5 space-y-4 pb-8">
									<IntegrationHealthSummary company={activeCompany} />

									{/* Access Suite group: Finance, Timesheets, Expense, Purchasing */}
									<div
										className={cn(
											"rounded-lg border",
											SUITE_FINANCE_PRODUCTS.includes(
												draft.finance.product as FinanceProduct,
											)
												? "border-primary/30 bg-primary/[0.03]"
												: "border-border bg-muted/20",
										)}
									>
										<div
											className={cn(
												"flex items-center gap-2 px-4 py-2.5 border-b",
												SUITE_FINANCE_PRODUCTS.includes(
													draft.finance.product as FinanceProduct,
												)
													? "border-primary/20 bg-primary/[0.05] rounded-t-lg"
													: "border-border rounded-t-lg",
											)}
										>
											{SUITE_FINANCE_PRODUCTS.includes(
												draft.finance.product as FinanceProduct,
											) ? (
												<>
													<Link2 className="h-3.5 w-3.5 text-primary shrink-0" />
													<span className="text-xs font-semibold text-primary tracking-wide uppercase">
														Access Suite
													</span>
													<Badge
														variant="outline"
														className="ml-1 border-primary/40 text-primary bg-primary/5 text-[10px] px-1.5 py-0 h-4"
													>
														Active
													</Badge>
												</>
											) : (
												<span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
													Finance, Timesheets, Expense &amp; Purchasing
												</span>
											)}
										</div>
										<div className="p-4 space-y-3">
											<div>
												<IntegrationSubSection
													companyId={activeCompany.id}
													companyName={activeCompany.name}
													type="finance"
													config={activeCompany.financeIntegration}
													draft={draft.finance}
													onDraftChange={handleIntegrationDraftChange}
													onSaved={handleRowSaved}
												/>
											</div>
											<div>
												<IntegrationSubSection
													companyId={activeCompany.id}
													companyName={activeCompany.name}
													type="timesheet"
													config={activeCompany.timesheetIntegration}
													draft={draft.timesheet}
													onDraftChange={handleIntegrationDraftChange}
													onSaved={handleRowSaved}
													financeProduct={draft.finance.product}
												/>
											</div>
											<div>
												<IntegrationSubSection
													companyId={activeCompany.id}
													companyName={activeCompany.name}
													type="purchasing"
													config={activeCompany.purchasingIntegration}
													draft={draft.purchasing}
													onDraftChange={handleIntegrationDraftChange}
													onSaved={handleRowSaved}
													financeProduct={draft.finance.product}
												/>
											</div>
											<div>
												<IntegrationSubSection
													companyId={activeCompany.id}
													companyName={activeCompany.name}
													type="expense"
													config={activeCompany.expenseIntegration}
													draft={draft.expense}
													onDraftChange={handleIntegrationDraftChange}
													onSaved={handleRowSaved}
												/>
											</div>
										</div>
									</div>

									{/* Scheduler group: standalone */}
									<div className="rounded-lg border border-border bg-muted/20">
										<div className="flex items-center gap-2 px-4 py-2.5 border-b border-border rounded-t-lg">
											<span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
												Scheduler
											</span>
										</div>
										<div className="p-4">
											<IntegrationSubSection
												companyId={activeCompany.id}
												companyName={activeCompany.name}
												type="planner"
												config={activeCompany.plannerIntegration}
												draft={draft.planner}
												onDraftChange={handleIntegrationDraftChange}
												onSaved={handleRowSaved}
											/>
										</div>
									</div>
								</div>
							</TabsContent>

							{/* Tab 2: Details */}
							<TabsContent
								value="details"
								className="flex-1 overflow-y-auto mt-0"
							>
								<div className="px-6 py-5 space-y-6">
									<CompanyDetailsSection
										draft={draft}
										onDraftChange={updateDraftField}
									/>
								</div>
							</TabsContent>

							{/* Tab 3: Users with Access */}
							<TabsContent
								value="users"
								className="flex-1 overflow-y-auto mt-0"
							>
								<div className="px-6 py-5 pb-8">
									<UsersWithAccessSection
										company={activeCompany}
										onUserCountChange={handleUserCountChange}
									/>
								</div>
							</TabsContent>
							{/* Tab 4: Sync Log */}
							<TabsContent
								value="sync-log"
								className="flex-1 overflow-y-auto mt-0"
							>
								<div className="px-6 pt-3 pb-8">
									<SyncLogSection
										ref={syncLogRef}
										companyId={activeCompany.id}
										page={syncLogPage}
										onPageChange={setSyncLogPage}
										onPageInfoChange={setSyncLogPageInfo}
									/>
								</div>
							</TabsContent>

							{/* Sticky footer — Sync Log pagination or Save All Changes */}
							{activeTab === "sync-log" ? (
								syncLogPageInfo.totalPages > 1 && (
									<div className="border-t px-6 py-3 shrink-0 flex items-center justify-between gap-3 bg-background text-xs text-muted-foreground">
										<span>
											Page {syncLogPage} of {syncLogPageInfo.totalPages} (
											{syncLogPageInfo.totalCount} entries)
										</span>
										<div className="flex items-center gap-1">
											<Button
												variant="ghost"
												size="sm"
												className="h-7 w-7 p-0"
												disabled={syncLogPage <= 1 || syncLogPageInfo.loading}
												onClick={() =>
													setSyncLogPage((p) => Math.max(1, p - 1))
												}
											>
												<ChevronLeft className="h-3.5 w-3.5" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="h-7 w-7 p-0"
												disabled={
													syncLogPage >= syncLogPageInfo.totalPages ||
													syncLogPageInfo.loading
												}
												onClick={() => setSyncLogPage((p) => p + 1)}
											>
												<ChevronRight className="h-3.5 w-3.5" />
											</Button>
										</div>
									</div>
								)
							) : (
								<div className="border-t px-6 py-3 shrink-0 flex items-center justify-between gap-3 bg-background">
									{isDirty ? (
										<p className="text-xs text-muted-foreground">
											Unsaved changes
										</p>
									) : (
										<p className="text-xs text-muted-foreground">
											All changes saved
										</p>
									)}
									<Button
										onClick={handleSaveAll}
										disabled={isSaving || !isDirty}
										size="sm"
										className="gap-1.5"
									>
										{isSaving ? (
											<>
												<Loader2 className="h-3.5 w-3.5 animate-spin" />
												Saving…
											</>
										) : (
											<>
												<Save className="h-3.5 w-3.5" />
												Save All Changes
											</>
										)}
									</Button>
								</div>
							)}
						</Tabs>
					)}
				</SheetContent>
			</Sheet>

			{/* Discard changes confirmation */}
			<AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
						<AlertDialogDescription>
							You have unsaved changes to this company. Closing now will discard
							them permanently.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setShowDiscardDialog(false)}>
							Keep Editing
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDiscard}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Discard Changes
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

export default CompanySheet;
